import * as XLSX from 'xlsx';
import type { Employee, OrganizationUnitNode } from '../../types/organizationWorkbench';

export const MAX_EMPLOYEE_IMPORT_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_EMPLOYEE_IMPORT_ROWS = 1_000;

export const EMPLOYEE_WORKBOOK_HEADERS = {
  employeeCode: '员工编号*',
  employeeName: '员工姓名*',
  roleCode: '岗位编码',
  unitId: '组织单元ID',
  unitName: '组织单元名称',
  employmentStatus: '状态',
  hireDate: '入职日期',
  shopfloorBaselinePct: '基础工时占比（%）',
  shopfloorUpperPct: '工时上限占比（%）',
} as const;

const WORKBOOK_SHEET_NAME = '员工';
const EXPORT_HEADERS = Object.values(EMPLOYEE_WORKBOOK_HEADERS);

export interface EmployeeImportSourceRow {
  row: number;
  employeeCode: string;
  employeeName: string;
  roleCode: string;
  unitId: number | null;
  unitIdError: string | null;
  employmentStatus: string;
  hireDate: string | null;
  hireDateError: string | null;
  shopfloorBaselinePct: number | null;
  shopfloorBaselinePctError: string | null;
  shopfloorUpperPct: number | null;
  shopfloorUpperPctError: string | null;
}

export interface EmployeeImportPreviewRow extends EmployeeImportSourceRow {
  issues: string[];
}

export interface EmployeeImportRole {
  roleCode: string;
}

export interface EmployeeImportValidationOptions {
  existingEmployeeCodes: string[];
  roles: EmployeeImportRole[];
  units: Pick<OrganizationUnitNode, 'id' | 'unitName'>[];
  defaultUnitId: number | null;
}

const statusAliases: Record<string, string> = {
  ACTIVE: 'ACTIVE',
  '在职': 'ACTIVE',
  VACATION: 'VACATION',
  '休假': 'VACATION',
  'ON LEAVE': 'ON LEAVE',
  '请假': 'ON LEAVE',
  RESIGNED: 'RESIGNED',
  '离职': 'RESIGNED',
};

const getText = (value: unknown): string => String(value ?? '').trim();

const toDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDate = (value: unknown): { value: string | null; error: string | null } => {
  if (value === null || value === undefined || value === '') return { value: null, error: null };
  if (value instanceof Date && !Number.isNaN(value.valueOf())) return { value: toDateString(value), error: null };

  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      return {
        value: `${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`,
        error: null,
      };
    }
  }

  const text = getText(value);
  const match = /^(\d{4})[-/]?(\d{1,2})[-/]?(\d{1,2})$/.exec(text);
  if (!match) return { value: null, error: '入职日期格式应为 YYYY-MM-DD' };

  const [year, month, day] = match.slice(1).map(Number);
  const candidate = new Date(year, month - 1, day);
  if (candidate.getFullYear() !== year || candidate.getMonth() !== month - 1 || candidate.getDate() !== day) {
    return { value: null, error: '入职日期无效' };
  }
  return { value: toDateString(candidate), error: null };
};

const parseOptionalPositiveInteger = (value: unknown): { value: number | null; error: string | null } => {
  if (value === null || value === undefined || value === '') return { value: null, error: null };
  const parsed = Number(getText(value));
  if (!Number.isInteger(parsed) || parsed <= 0) return { value: null, error: '组织单元ID必须是正整数' };
  return { value: parsed, error: null };
};

const parseOptionalPercent = (value: unknown, label: string): { value: number | null; error: string | null } => {
  if (value === null || value === undefined || value === '') return { value: null, error: null };
  const parsed = Number(getText(value).replace('%', ''));
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return { value: null, error: `${label}应为 0 至 100 的数字` };
  return { value: parsed / 100, error: null };
};

const getCellValue = (row: Record<string, unknown>, header: string, aliases: string[] = []): unknown => {
  const candidates = [header, header.replace('*', ''), ...aliases];
  const key = Object.keys(row).find((item) => candidates.includes(item.trim()));
  return key ? row[key] : null;
};

const normalizeStatus = (value: unknown): string => {
  const normalized = getText(value).toUpperCase();
  return statusAliases[normalized] ?? normalized;
};

const getDateForExport = (value: string | null | undefined): string => {
  const date = getText(value);
  return date ? date.slice(0, 10) : '';
};

const getDateStamp = (): string => new Date().toISOString().slice(0, 10);

const applyColumnWidths = (worksheet: XLSX.WorkSheet): void => {
  worksheet['!cols'] = [
    { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 22 },
    { wch: 16 }, { wch: 16 }, { wch: 20 }, { wch: 20 },
  ];
};

export const downloadEmployeeTemplate = (): void => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet([], { header: EXPORT_HEADERS });
  applyColumnWidths(worksheet);
  XLSX.utils.book_append_sheet(workbook, worksheet, WORKBOOK_SHEET_NAME);

  const instructions = XLSX.utils.aoa_to_sheet([
    ['字段', '填写说明'],
    [EMPLOYEE_WORKBOOK_HEADERS.employeeCode, '必填；系统内不能重复。建议将该列设置为文本，避免前导零丢失。'],
    [EMPLOYEE_WORKBOOK_HEADERS.employeeName, '必填。'],
    [EMPLOYEE_WORKBOOK_HEADERS.roleCode, '可选；必须是“岗位配置”中已有的岗位编码，留空时使用 FRONTLINE。'],
    [EMPLOYEE_WORKBOOK_HEADERS.unitId, '可选；填写有效的组织单元 ID。留空时归属到当前页面选中的组织单元。'],
    [EMPLOYEE_WORKBOOK_HEADERS.unitName, '导出时仅供识别，导入时不参与匹配。'],
    [EMPLOYEE_WORKBOOK_HEADERS.employmentStatus, '可选；可填 ACTIVE、VACATION、ON LEAVE、RESIGNED，或中文在职、休假、请假、离职。'],
    [EMPLOYEE_WORKBOOK_HEADERS.hireDate, '可选；格式 YYYY-MM-DD。'],
    [EMPLOYEE_WORKBOOK_HEADERS.shopfloorBaselinePct, '可选；填写 0 至 100，例如 80 表示 80%。'],
    [EMPLOYEE_WORKBOOK_HEADERS.shopfloorUpperPct, '可选；填写 0 至 100，例如 100 表示 100%。'],
  ]);
  instructions['!cols'] = [{ wch: 22 }, { wch: 88 }];
  XLSX.utils.book_append_sheet(workbook, instructions, '填写说明');
  XLSX.writeFile(workbook, '组织人员导入模板.xlsx');
};

export const downloadEmployeesWorkbook = (employees: Employee[], scopeName: string): void => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(employees.map((employee) => ({
    [EMPLOYEE_WORKBOOK_HEADERS.employeeCode]: employee.employee_code,
    [EMPLOYEE_WORKBOOK_HEADERS.employeeName]: employee.employee_name,
    [EMPLOYEE_WORKBOOK_HEADERS.roleCode]: employee.org_role ?? '',
    [EMPLOYEE_WORKBOOK_HEADERS.unitId]: employee.unit_id ?? '',
    [EMPLOYEE_WORKBOOK_HEADERS.unitName]: employee.unit_name ?? '',
    [EMPLOYEE_WORKBOOK_HEADERS.employmentStatus]: employee.employment_status ?? 'ACTIVE',
    [EMPLOYEE_WORKBOOK_HEADERS.hireDate]: getDateForExport(employee.hire_date),
    [EMPLOYEE_WORKBOOK_HEADERS.shopfloorBaselinePct]: employee.shopfloor_baseline_pct == null ? '' : Math.round(employee.shopfloor_baseline_pct * 100),
    [EMPLOYEE_WORKBOOK_HEADERS.shopfloorUpperPct]: employee.shopfloor_upper_pct == null ? '' : Math.round(employee.shopfloor_upper_pct * 100),
  })), { header: EXPORT_HEADERS });
  applyColumnWidths(worksheet);
  XLSX.utils.book_append_sheet(workbook, worksheet, WORKBOOK_SHEET_NAME);
  XLSX.writeFile(workbook, `${scopeName}_组织人员_${getDateStamp()}.xlsx`);
};

export const readEmployeeImportWorkbook = async (file: File): Promise<EmployeeImportSourceRow[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const worksheet = workbook.Sheets[WORKBOOK_SHEET_NAME] ?? workbook.Sheets[workbook.SheetNames[0]];
  if (!worksheet) throw new Error('未找到可读取的工作表');

  const headerRow = XLSX.utils.sheet_to_json<unknown[]>(worksheet, { header: 1, blankrows: false })[0] ?? [];
  const headers = headerRow.map((header) => getText(header));
  const hasEmployeeCode = headers.includes(EMPLOYEE_WORKBOOK_HEADERS.employeeCode) || headers.includes(EMPLOYEE_WORKBOOK_HEADERS.employeeCode.replace('*', ''));
  const hasEmployeeName = headers.includes(EMPLOYEE_WORKBOOK_HEADERS.employeeName) || headers.includes(EMPLOYEE_WORKBOOK_HEADERS.employeeName.replace('*', ''));
  if (!hasEmployeeCode || !hasEmployeeName) throw new Error('缺少“员工编号*”或“员工姓名*”列，请使用系统模板');

  const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: null, raw: true });
  if (rawRows.length > MAX_EMPLOYEE_IMPORT_ROWS) throw new Error(`单次最多导入 ${MAX_EMPLOYEE_IMPORT_ROWS} 名员工`);

  return rawRows.map((rawRow, index) => {
    const parsedUnitId = parseOptionalPositiveInteger(getCellValue(rawRow, EMPLOYEE_WORKBOOK_HEADERS.unitId));
    const parsedHireDate = parseDate(getCellValue(rawRow, EMPLOYEE_WORKBOOK_HEADERS.hireDate));
    const parsedBaselinePct = parseOptionalPercent(getCellValue(rawRow, EMPLOYEE_WORKBOOK_HEADERS.shopfloorBaselinePct, ['基础工时占比']), '基础工时占比');
    const parsedUpperPct = parseOptionalPercent(getCellValue(rawRow, EMPLOYEE_WORKBOOK_HEADERS.shopfloorUpperPct, ['工时上限占比']), '工时上限占比');

    return {
      row: index + 2,
      employeeCode: getText(getCellValue(rawRow, EMPLOYEE_WORKBOOK_HEADERS.employeeCode)),
      employeeName: getText(getCellValue(rawRow, EMPLOYEE_WORKBOOK_HEADERS.employeeName)),
      roleCode: getText(getCellValue(rawRow, EMPLOYEE_WORKBOOK_HEADERS.roleCode)).toUpperCase() || 'FRONTLINE',
      unitId: parsedUnitId.value,
      unitIdError: parsedUnitId.error,
      employmentStatus: normalizeStatus(getCellValue(rawRow, EMPLOYEE_WORKBOOK_HEADERS.employmentStatus)) || 'ACTIVE',
      hireDate: parsedHireDate.value,
      hireDateError: parsedHireDate.error,
      shopfloorBaselinePct: parsedBaselinePct.value,
      shopfloorBaselinePctError: parsedBaselinePct.error,
      shopfloorUpperPct: parsedUpperPct.value,
      shopfloorUpperPctError: parsedUpperPct.error,
    };
  });
};

export const validateEmployeeImportRows = (
  rows: EmployeeImportSourceRow[],
  { existingEmployeeCodes, roles, units, defaultUnitId }: EmployeeImportValidationOptions,
): EmployeeImportPreviewRow[] => {
  const existingCodes = new Set(existingEmployeeCodes.map((code) => getText(code).toUpperCase()).filter(Boolean));
  const validRoleCodes = new Set(roles.map((role) => getText(role.roleCode).toUpperCase()).filter(Boolean));
  const validUnitIds = new Set(units.map((unit) => unit.id));
  const seenCodes = new Set<string>();

  return rows.map((row) => {
    const issues: string[] = [];
    const employeeCode = row.employeeCode.toUpperCase();
    const unitId = row.unitId ?? defaultUnitId;

    if (!employeeCode) issues.push('员工编号不能为空');
    else if (seenCodes.has(employeeCode)) issues.push('文件内员工编号重复');
    else if (existingCodes.has(employeeCode)) issues.push('员工编号已存在');
    if (employeeCode) seenCodes.add(employeeCode);

    if (!row.employeeName) issues.push('员工姓名不能为空');
    if (!validRoleCodes.has(row.roleCode)) issues.push(`岗位编码“${row.roleCode}”不存在`);
    if (row.unitIdError) issues.push(row.unitIdError);
    if (!unitId || !validUnitIds.has(unitId)) issues.push('组织单元ID不存在，请填写有效值或在当前组织单元下导入');
    if (!statusAliases[row.employmentStatus]) issues.push('状态不支持');
    if (row.hireDateError) issues.push(row.hireDateError);
    if (row.shopfloorBaselinePctError) issues.push(row.shopfloorBaselinePctError);
    if (row.shopfloorUpperPctError) issues.push(row.shopfloorUpperPctError);

    return { ...row, unitId, issues };
  });
};
